import { Injectable } from '@angular/core';

export interface ICookieOptions {
  /** Days until the cookie expires. Omit for a session cookie. */
  expiresDays?: number;
  path?: string;
  sameSite?: 'Strict' | 'Lax' | 'None';
  /** Only transmit over HTTPS. Cookies with `secure` are dropped on plain http (e.g. localhost). */
  secure?: boolean;
}

/**
 * Thin wrapper around `document.cookie` for reading/writing/deleting cookies.
 *
 * Note: cookies set from JavaScript cannot be `HttpOnly`, so they are still
 * readable by scripts (no XSS advantage over localStorage). For true HttpOnly
 * protection the server must issue the cookie via a `Set-Cookie` response header.
 */
@Injectable({ providedIn: 'root' })
export class CookieService {
  set(name: string, value: string, options: ICookieOptions = {}): void {
    const { expiresDays, path = '/', sameSite = 'Lax', secure = false } = options;
    let cookie = `${encodeURIComponent(name)}=${encodeURIComponent(value)}; path=${path}; SameSite=${sameSite}`;
    if (expiresDays != null) {
      const expires = new Date();
      expires.setTime(expires.getTime() + expiresDays * 24 * 60 * 60 * 1000);
      cookie += `; expires=${expires.toUTCString()}`;
    }
    if (secure) cookie += '; Secure';
    document.cookie = cookie;
  }

  get(name: string): string | null {
    const prefix = `${encodeURIComponent(name)}=`;
    const match = document.cookie.split('; ').find((entry) => entry.startsWith(prefix));
    return match ? decodeURIComponent(match.substring(prefix.length)) : null;
  }

  delete(name: string, path = '/'): void {
    document.cookie = `${encodeURIComponent(name)}=; path=${path}; expires=Thu, 01 Jan 1970 00:00:00 GMT`;
  }
}
